import { useCallback, useMemo, useState } from 'react'
import { TableReportTeamsColumnsDefinition } from './components/TableReportTeamsColumnsDefinition'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import { useReportTeams } from '@/app/hooks/useReportTeams'
import * as XLSX from 'xlsx'
import {
  ReportTeams,
  ReportTeamsParams,
} from '@/app/services/reportService/reportTeams'
import { reportService } from '@/app/services/reportService'
import { FiltersType } from '../components/FilterReports'
import { useAuth } from '@/app/hooks/useAuth'

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportTeamsController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } = useReportTeams()
  const { isAdmin, isManager } = useAuth()

  const { columns } = TableReportTeamsColumnsDefinition()

  const table = useReactTable({
    data: data?.teams || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })
  const fetchAllData = useCallback(async (): Promise<ReportTeams[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    const batchSize = AMOUNT_REPORTS_FETCH
    const total = data.pagination.total
    let currentSkip = 0
    let allData: ReportTeams[] = []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const batches: Promise<any>[] = []

    let completedBatches = 0

    while (total > currentSkip) {
      const exportParams: ReportTeamsParams = {
        ...params,
        limit: batchSize,
        skip: currentSkip,
      }

      const batchPromise = reportService
        .reportTeams(exportParams)
        .then((batchData) => {
          allData = [...allData, ...batchData.teams]

          completedBatches += 1

          const progress = Number(
            Math.min(
              ((completedBatches * batchSize) / total) * 100,
              100,
            ).toFixed(0),
          )
          setExportProgress(progress)
        })
        .catch((error) => {
          console.error('Error fetching batch data:', error)
        })

      batches.push(batchPromise)

      currentSkip += batchSize
    }

    try {
      await Promise.all(batches)
    } catch (error) {
      console.error('Error awaiting batch resolutions:', error)
    }

    setIsExporting(false)
    return allData
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    setIsExporting(true)
    const fetchedData = await fetchAllData()

    const formattedData = fetchedData.map((team) => ({
      name: team.name || '',
      manager: team.manager || 'Sem Gerente',
      supervisor: team.supervisor || 'Sem Supervisor',
      leader: team.leader || 'Sem Lider',
      members: team.members || 0,
      transactions: team.transactions || 0,
      transactionsValue: +(team.transactions_value / 100).toFixed(2) || 0,
      comissionsValue: +(team.commission_value / 100).toFixed(2) || 0,
    }))

    setIsExporting(false)

    if (formattedData.length > 0) {
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(formattedData)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
      XLSX.utils.sheet_add_aoa(worksheet, [
        [
          'Nome',
          'Gerente',
          'Supervisor',
          'Líder',
          'Nº Vendedores',
          'Nº Transações',
          'Total movimentado',
          'Total em comissões',
        ],
      ])
      XLSX.writeFile(workbook, 'teams-reports.xlsx')
    }
  }, [fetchAllData])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ['Date']

    if (isAdmin) result.push('Managers', 'Supervisors')
    if (isManager) result.push('Supervisors')

    result.push('Value')

    return result
  }, [isAdmin, isManager])

  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
